import { IconButton, InputAdornment, TextField } from "@mui/material";
import { controlBandFieldSx } from "../../theme/compact.js";
import type { ChangeEvent, ReactElement } from "react";
import { SearchIcon, XIcon } from "@aeriosiq/ui/icons";

/**
 * `<SearchField>` — canonical filter-row search input.
 *
 * Spec: `.vibe/60_TEMPLATE_DATA_PAGE.md` §"Sub-template 5 / SearchField".
 *
 * - 240 px wide, `size="small"`.
 * - `type="search"`, accessible name = the `ariaLabel` prop (defaults to
 *   `"Search"`).
 * - Magnifier `startAdornment`. Clear-× `endAdornment` when value
 *   non-empty.
 * - Owns NO debounce — Christian's per-page URL-write semantics differ
 *   (Sites debounces 500 ms; Data Acquisitions debounces 400 ms) so the
 *   caller stays in control.
 */
export interface SearchFieldProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  /** The id of a helper text that describes what the search matches (`aria-describedby`). */
  describedBy?: string;
}

export function SearchField({
  value,
  onChange,
  placeholder = "Search",
  ariaLabel = "Search",
  describedBy,
}: SearchFieldProps): ReactElement {
  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    onChange(e.target.value);
  };
  return (
    <TextField
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      size="small"
      slotProps={{
        htmlInput: {
          "aria-label": ariaLabel,
          ...(describedBy ? { "aria-describedby": describedBy } : {}),
          type: "search",
        },
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
            </InputAdornment>
          ),
          endAdornment:
            value.length > 0 ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  edge="end"
                  aria-label={`Clear ${ariaLabel.toLowerCase()}`}
                  onClick={() => {
                    onChange("");
                  }}
                >
                  <XIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
        },
      }}
      sx={{
        width: 240,
        flexShrink: 0,
        // The dense band (D77 compact, 32px). Without this the control
        // takes MUI's `size="small"` default, which is 40px for a field —
        // not one of the three sanctioned sizes, simply whatever the
        // library does. Audit, 2026-09-23.
        ...controlBandFieldSx,
      }}
    />
  );
}
